import type { FC } from 'react';
import React, { memo, useCallback, useEffect, useMemo, useContext } from 'react';
import { useIntl } from 'react-intl-next';

import type { ADNode } from '@atlaskit/editor-common/validator';
import type { DocNode } from '@atlaskit/adf-schema';
import type { ExtensionHandlers, ExtensionParams } from '@atlaskit/editor-common/extensions';
import { RendererActionsContext } from '@atlaskit/renderer/actions';
import { ProviderFactory } from '@atlaskit/editor-common/provider-factory';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { ValidationContextProvider } from '@atlaskit/renderer';
import { getPageElementCounts } from '@atlaskit/editor-common/utils';

import { useRendererAnnotationProviderOptions } from '@confluence/annotation-provider';
import { useSpaceId, useSpaceKey } from '@confluence/space-utils';
import { useContentType, usePageContentId } from '@confluence/page-context';
import { useSessionData } from '@confluence/session-data';
import { ADFRenderer } from '@confluence/adf-renderer';
import { RendererExtensionContext } from '@confluence/content-renderer-extension-context/entry-points/RendererExtensionContext';

import type { LegacyContentMacroParams } from './LegacyContentMacroParams';

export interface NBMRendererProps {
	node: ExtensionParams<LegacyContentMacroParams>;
}

const DEFAULT_DOCUMENT: DocNode = {
	version: 1,
	type: 'doc',
	content: [
		{
			type: 'paragraph',
			content: [],
		},
	],
};

export const NBMRenderer: FC<NBMRendererProps> = memo(({ node }) => {
	const spaceId = useSpaceId();
	const spaceKey = useSpaceKey();
	const [contentId] = usePageContentId();
	const [contentType = 'page'] = useContentType();
	const { environment, cloudId, userId, activationId } = useSessionData();
	const { getExtensionHandlers, populateExtensionProviders } = useContext(RendererExtensionContext);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const intl = useIntl();

	const document = node?.parameters?.nestedContent || DEFAULT_DOCUMENT;
	const appearance = 'full-width';

	/**
	 * Both `getExtensionHandlers` & `populateExtensionProviders` don't type check properly because in
	 * `confluence/next/packages/content-renderer-extension-context/src/RendererExtensionContext.tsx`
	 * the `options` is set to `any`. The reason most like is because the type lives inside classic codebase, here:
	 * `confluence/packages/confluence-fabric-editor/src/lib/renderer-extensions/renderer-macro-extension-handler.tsx`
	 */
	const extensionHandlers = useMemo(
		() =>
			getExtensionHandlers?.(
				{
					adf: document as ADNode,
					contentId,
					spaceKey,
					contentType,
					appearance,
					referentialityDataSourceProvider: {},
				},
				createAnalyticsEvent,
			),
		[contentId, contentType, createAnalyticsEvent, document, getExtensionHandlers, spaceKey],
	);

	const annotationProvider = useRendererAnnotationProviderOptions({
		pageId: contentId ?? '',
		validAnnotationsCallback: () => {},
		triggerAnalyticsEvent: createAnalyticsEvent,
		updateDocument: () => {},
		isArchived: false,
	});

	const getDataProviders = useCallback(() => {
		const providerFactory = ProviderFactory.create({});

		if (populateExtensionProviders) {
			populateExtensionProviders(providerFactory, {
				activationId,
				accountId: userId,
				cloudId,
				containerId: spaceKey,
				contentId,
				contentType,
				createAnalyticsEvent,
				environment,
				extensionHandlers,
				intl,
				spaceKey,
				spaceId,
			});
		}

		return providerFactory;
	}, [
		populateExtensionProviders,
		activationId,
		userId,
		cloudId,
		spaceKey,
		contentId,
		contentType,
		createAnalyticsEvent,
		environment,
		extensionHandlers,
		intl,
		spaceId,
	]);

	const validationContextValue = useMemo(() => ({ skipValidation: true }), []);

	useEffect(() => {
		if (contentId && createAnalyticsEvent && document)
			createAnalyticsEvent({
				type: 'sendOperationalEvent',
				data: {
					action: 'rendererInitialised',
					actionSubject: 'legacyContentMacro',
					source: 'legacyContentMacro',
					attributes: {
						objectId: contentId,
						pageElementCounts: getPageElementCounts(document),
					},
				},
			}).fire();
	}, [contentId, createAnalyticsEvent, document, node.localId]);

	return (
		<ValidationContextProvider value={validationContextValue}>
			<RendererActionsContext>
				<ADFRenderer
					annotationProvider={annotationProvider}
					allowAnnotations
					appearance={appearance}
					allowHeadingAnchorLinks={false}
					contentId={contentId}
					dataProviders={getDataProviders()}
					document={document}
					extensionHandlers={extensionHandlers as unknown as ExtensionHandlers | undefined} // TODO: Fix type casting - https://product-fabric.atlassian.net/browse/ED-26255
				/>
			</RendererActionsContext>
		</ValidationContextProvider>
	);
});
